import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { CommentSection } from "../components/CommentSection";
import { FilePreview } from "../components/FilePreview";
import { useDeleteProject, useIncrementDownload, useMe, usePost, useToggleLike } from "../lib/api";
import { useAuthStore } from "../lib/auth-store";
import { isPreviewable } from "../lib/file-preview";
import { downloadFile } from "../lib/storage";

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

async function download(storagePath: string, fileName: string): Promise<void> {
  try {
    await downloadFile(storagePath, fileName);
  } catch {
    // download failures are non-fatal for the MVP
  }
}

function FileRow({
  file,
  onDownload,
}: {
  file: { id: string; originalName: string; size: number; storagePath: string };
  onDownload: () => void;
}) {
  const [preview, setPreview] = useState(false);
  const canPreview = isPreviewable(file.originalName);
  return (
    <li className="rounded-lg border border-slate-800 px-3 py-2 text-sm">
      <div className="flex items-center justify-between">
        <span className="truncate text-slate-200">{file.originalName}</span>
        <div className="flex shrink-0 items-center gap-3">
          <span className="font-mono text-xs text-slate-500">{formatBytes(file.size)}</span>
          {canPreview && (
            <button
              type="button"
              onClick={() => setPreview((v) => !v)}
              className="rounded-lg border border-cyan-400/40 px-3 py-1 text-xs text-cyan-200 transition hover:border-cyan-300"
            >
              미리보기
            </button>
          )}
          <button
            type="button"
            onClick={onDownload}
            className="btn-neon rounded-lg px-3 py-1 text-xs font-medium"
          >
            받기
          </button>
        </div>
      </div>
      {preview && <FilePreview storagePath={file.storagePath} fileName={file.originalName} />}
    </li>
  );
}

export function ProjectDetail() {
  const { id = "" } = useParams();
  const { data: post, isLoading, isError } = usePost(id);
  const fbUser = useAuthStore((s) => s.fbUser);
  const { data: me } = useMe(Boolean(fbUser));
  const del = useDeleteProject();
  const navigate = useNavigate();
  const like = useToggleLike(id);
  const incDownload = useIncrementDownload(id);
  const [showComments, setShowComments] = useState(false);
  const [showFiles, setShowFiles] = useState(false);
  const [shared, setShared] = useState(false);

  if (isLoading) {
    return <div className="mt-20 text-center font-mono text-sm text-cyan-300/70">불러오는 중…</div>;
  }
  if (isError || !post) {
    return <div className="mt-20 text-center text-slate-400">프로젝트를 찾을 수 없습니다.</div>;
  }

  const canDelete =
    me && (me.uid === post.authorUid || me.role === "admin" || me.role === "sub_admin");

  async function onDelete() {
    if (!post || !window.confirm("이 프로젝트를 삭제할까요?")) return;
    await del.mutateAsync(post.id);
    navigate("/");
  }

  return (
    <section className="mx-auto max-w-3xl fade-up">
      <div className="mb-4 flex items-center justify-between">
        <Link to="/" className="text-sm text-slate-400 transition hover:text-cyan-300">
          ← 목록으로
        </Link>
        {canDelete && (
          <div className="flex items-center gap-3">
            <Link
              to={`/projects/${post.id}/edit`}
              className="text-sm text-slate-400 transition hover:text-cyan-300"
            >
              수정
            </Link>
            <button
              type="button"
              onClick={onDelete}
              disabled={del.isPending}
              className="text-sm text-slate-400 transition hover:text-fuchsia-400"
            >
              삭제
            </button>
          </div>
        )}
      </div>

      <div className="glass neon-border rounded-2xl p-7">
        {post.categoryName && (
          <span className="rounded bg-cyan-400/10 px-2 py-0.5 font-mono text-[10px] text-cyan-300">
            {post.categoryName}
          </span>
        )}
        <h1 className="neon-text mt-2 text-3xl font-bold">{post.title}</h1>
        <div className="mt-2 flex items-center gap-3 text-xs text-slate-500">
          <Link to={`/users/${post.authorUid}`} className="hover:text-cyan-300">
            {post.authorNickname}
          </Link>
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-5 border-y border-slate-800 py-3 text-sm">
          <button
            type="button"
            onClick={() => like.mutate({ targetType: "post", targetId: post.id })}
            className={
              post.likedByMe
                ? "font-mono text-fuchsia-400"
                : "font-mono text-slate-400 transition hover:text-fuchsia-300"
            }
          >
            ❤ 좋아요 {post.likeCount}
          </button>
          <button
            type="button"
            onClick={() => setShowComments((v) => !v)}
            className="font-mono text-slate-400 transition hover:text-cyan-300"
          >
            💬 댓글 {post.commentCount}
          </button>
          <button
            type="button"
            onClick={() => setShowFiles((v) => !v)}
            className="font-mono text-slate-400 transition hover:text-cyan-300"
          >
            ⬇ 다운로드 {post.downloadCount}
          </button>
          <button
            type="button"
            onClick={async () => {
              await navigator.clipboard.writeText(window.location.href);
              setShared(true);
            }}
            className="font-mono text-slate-400 transition hover:text-cyan-300"
          >
            {shared ? "🔗 복사됨" : "🔗 공유"}
          </button>
        </div>

        {post.description && (
          <div className="mt-6">
            <h2 className="text-sm font-semibold text-cyan-300/80">프로젝트 소개</h2>
            <p className="mt-2 whitespace-pre-wrap text-sm text-slate-300">{post.description}</p>
          </div>
        )}
        {post.features && (
          <div className="mt-6">
            <h2 className="text-sm font-semibold text-cyan-300/80">주요 기능</h2>
            <p className="mt-2 whitespace-pre-wrap text-sm text-slate-300">{post.features}</p>
          </div>
        )}

        {showFiles && post.files.length > 0 && (
          <div className="mt-8 border-t border-slate-800 pt-5">
            <h2 className="text-sm font-semibold text-cyan-300/80">
              첨부 파일 ({post.files.length})
            </h2>
            <ul className="mt-3 flex flex-col gap-2">
              {post.files.map((f) => (
                <FileRow
                  key={f.id}
                  file={f}
                  onDownload={async () => {
                    await download(f.storagePath, f.originalName);
                    incDownload.mutate();
                  }}
                />
              ))}
            </ul>
          </div>
        )}

        {showComments && <CommentSection postId={post.id} />}
      </div>
    </section>
  );
}
